/**
 * COMMAND PALETTE COMPONENT
 * ========================
 * 
 * A command palette that responds to Cmd+K (Mac) / Ctrl+K (Windows)
 * Provides quick access to navigation, sign-out, and theme switching
 */

import { useState, useEffect, useCallback } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { useTheme } from "./ThemeProvider";
import {
  LayoutDashboard,
  Plus,
  FolderOpen,
  BarChart3,
  MessageSquare,
  BookOpen,
  Settings,
  HelpCircle,
  CreditCard,
  LogOut,
  Sun,
  Moon,
  Lightbulb,
  FileText,
} from "lucide-react";

interface CommandPaletteProps {
  isAuthenticated: boolean;
  currentPage: string;
  onNavigate: {
    navigateToDashboard: () => void;
    navigateToNewDecision: () => void;
    navigateToMyDecisions: () => void;
    navigateToAnalytics: () => void;
    navigateToReviews: () => void;
    navigateToLog: () => void;
    navigateToSettings: () => void;
    navigateToHelp: () => void;
    navigateToPricing: () => void;
    navigateToAppBlog: () => void;
    navigateToAppBestPractices: () => void;
    handleSignOut: () => void;
  };
}

export function CommandPalette({ isAuthenticated, currentPage, onNavigate }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  // Handle keyboard shortcuts
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = useCallback((callback: () => void) => {
    setOpen(false);
    callback();
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  const navigationItems = isAuthenticated ? [
    {
      group: "Navigation",
      items: [
        {
          icon: LayoutDashboard,
          label: "Dashboard",
          action: () => handleSelect(onNavigate.navigateToDashboard),
          current: currentPage === "dashboard"
        },
        {
          icon: Plus,
          label: "New Decision",
          action: () => handleSelect(onNavigate.navigateToNewDecision),
          current: currentPage === "new-decision"
        },
        {
          icon: FolderOpen,
          label: "My Decisions",
          action: () => handleSelect(onNavigate.navigateToMyDecisions),
          current: currentPage === "my-decisions"
        },
        {
          icon: BarChart3,
          label: "Analytics",
          action: () => handleSelect(onNavigate.navigateToAnalytics),
          current: currentPage === "analytics"
        },
        {
          icon: MessageSquare,
          label: "Reviews",
          action: () => handleSelect(onNavigate.navigateToReviews),
          current: currentPage === "reviews"
        },
        {
          icon: BookOpen,
          label: "Decision Log",
          action: () => handleSelect(onNavigate.navigateToLog),
          current: currentPage === "log"
        },
      ]
    },
    {
      group: "Resources",
      items: [
        {
          icon: FileText,
          label: "Blog",
          action: () => handleSelect(onNavigate.navigateToAppBlog),
          current: currentPage === "app-blog"
        },
        {
          icon: Lightbulb,
          label: "Best Practices",
          action: () => handleSelect(onNavigate.navigateToAppBestPractices),
          current: currentPage === "app-best-practices"
        },
      ]
    },
    {
      group: "Account",
      items: [
        {
          icon: Settings,
          label: "Settings",
          action: () => handleSelect(onNavigate.navigateToSettings),
          current: currentPage === "settings"
        },
        {
          icon: HelpCircle,
          label: "Help",
          action: () => handleSelect(onNavigate.navigateToHelp),
          current: currentPage === "help"
        },
        {
          icon: CreditCard,
          label: "Pricing",
          action: () => handleSelect(onNavigate.navigateToPricing),
          current: currentPage === "pricing"
        },
      ]
    }
  ] : [];

  const systemItems = [
    {
      group: "System",
      items: [
        {
          icon: theme === "light" ? Moon : Sun,
          label: `Switch to ${theme === "light" ? "Dark" : "Light"} Mode`,
          action: () => handleSelect(toggleTheme),
          current: false
        },
        ...(isAuthenticated ? [{
          icon: LogOut,
          label: "Sign Out",
          action: () => handleSelect(onNavigate.handleSignOut),
          current: false
        }] : [])
      ]
    }
  ];

  const allGroups = [...navigationItems, ...systemItems];

  return (
    <CommandDialog 
      open={open} 
      onOpenChange={setOpen}
      title="Command Palette"
      description="Quickly navigate and access features"
    >
      <CommandInput placeholder="Type a command or search..." className="px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 pr-4 pb-1" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {allGroups.map((group) => (
          <CommandGroup key={group.group} heading={group.group}>
            {group.items.map((item) => (
              <CommandItem
                key={item.label}
                onSelect={item.action}
                className={item.current ? "bg-accent text-accent-foreground" : ""}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
                {item.current && (
                  <span className="ml-auto text-xs text-muted-foreground">Current</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}